// Per-player view of the game state. The canonical engine state is
// "god view" — it carries both sides' hand and deck contents. That's
// fine for the live game (one process, one UI, the player only sees
// their own hand because the renderer chooses what to show) and for
// server-of-record bookkeeping, but it leaks information to any
// downstream consumer that thinks of itself as a single player:
//
//   - The AI making decisions on behalf of one side.
//   - PvP clients (Phase 5) — the server projects per-client before
//     sending the state down the wire.
//
// `projectForSide(state, side)` returns a `cloneState`-derived view in
// which the OTHER side's hand and deck instances are masked:
//   - `cardId` replaced by UNKNOWN_CARD_ID
//   - `isUnknown` set to true
//   - `currentPower` / `armor` zeroed (an unknown card has no public stats)
//
// Other zones (ring, discard, attackLine) and other public state (HP,
// spirit, clock, combatState, voice latches, rng) remain as-is. Anything
// that's visible in the live UI is visible in every perspective.
//
// The natural safety net for accidental info leaks is `getCardDef`:
// looking up the sentinel cardId throws "Card not found: __unknown__".
// AI / search code that has no business reading an unknown card will
// crash loudly rather than silently using stale data.

import type { GameState, PlayerSide } from '../types.js';
import { cloneState } from './GameState.js';

export const UNKNOWN_CARD_ID = '__unknown__';

/** Returns a cloned state filtered for `side`'s perspective. Masks the
 *  opposite side's hand + deck contents. Idempotent: projecting an
 *  already-projected state is safe (the same instances stay masked). */
export function projectForSide(state: GameState, side: PlayerSide): GameState {
  const projected = cloneState(state);
  const hiddenSide: PlayerSide = side === 'player' ? 'opponent' : 'player';
  const hiddenPlayer = hiddenSide === 'player' ? projected.player : projected.opponent;
  const hiddenIds = new Set<string>([...hiddenPlayer.hand, ...hiddenPlayer.deck]);
  for (const id of hiddenIds) {
    const inst = projected.cardInstances.get(id);
    if (!inst) continue;
    inst.cardId = UNKNOWN_CARD_ID;
    inst.isUnknown = true;
    // An unknown card has no public stats. Zero them so any silent read
    // produces a 0 (which surfaces as an obviously-wrong value in any
    // power computation) rather than carrying a stale snapshot.
    inst.currentPower = 0;
    inst.armor = 0;
  }
  return projected;
}
